import { useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { adminSetBlockedDates } from "../../lib/api";

function DateToggleCalendar({ selected, onToggle }) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const [view, setView] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const lastDay = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();
  const startWeekday = (new Date(view).getDay() + 6) % 7;
  const days = [];
  for (let i = 0; i < startWeekday; i++) days.push(null);
  for (let d = 1; d <= lastDay; d++) days.push(new Date(view.getFullYear(), view.getMonth(), d));

  return (
    <div className="select-none">
      <div className="flex items-center justify-between mb-4">
        <button data-testid="dt-prev" onClick={() => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1))} className="w-8 h-8 border border-[var(--ed-rule)] hover:border-[var(--ed-ink)] flex items-center justify-center text-[var(--ed-ink-soft)] text-sm rounded-sm">‹</button>
        <h4 className="font-serif text-base text-[var(--ed-ink)]">{view.toLocaleString("en-GB", { month: "long", year: "numeric" })}</h4>
        <button data-testid="dt-next" onClick={() => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))} className="w-8 h-8 border border-[var(--ed-rule)] hover:border-[var(--ed-ink)] flex items-center justify-center text-[var(--ed-ink-soft)] text-sm rounded-sm">›</button>
      </div>
      <div className="grid grid-cols-7 gap-1 mb-1 text-[0.6rem] text-[var(--ed-ink-muted)] text-center">
        {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => <div key={i} className="py-1">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {days.map((d, i) => {
          if (!d) return <div key={i} />;
          const k = fmt(d);
          const isSelected = selected.has(k);
          const past = d < today;
          return (
            <button
              key={i}
              data-testid={`dt-day-${k}`}
              type="button"
              onClick={() => !past && onToggle(k)}
              disabled={past}
              className={`aspect-square text-sm flex items-center justify-center transition-colors rounded-sm
                ${past ? "text-[var(--ed-ink-muted)]/40 cursor-not-allowed" : ""}
                ${!past && !isSelected ? "text-[var(--ed-ink-soft)] hover:bg-[var(--ed-paper)] hover:text-[var(--ed-ink)] border border-transparent" : ""}
                ${isSelected ? "bg-[var(--ed-emerald)] text-white font-medium" : ""}
              `}
            >{d.getDate()}</button>
          );
        })}
      </div>
    </div>
  );
}

export default function DatesEditor({ property, onClose, onSaved }) {
  const [dates, setDates] = useState(new Set(property.blocked_dates || []));
  const [saving, setSaving] = useState(false);

  const toggle = (k) => {
    const next = new Set(dates);
    if (next.has(k)) next.delete(k); else next.add(k);
    setDates(next);
  };

  const onSave = async () => {
    setSaving(true);
    try {
      await adminSetBlockedDates(property.id, Array.from(dates));
      toast.success("Blocked dates updated");
      onSaved();
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto">
      <div data-testid="dates-editor" className="w-full max-w-md my-8 bg-[var(--ed-bg)] border border-[var(--ed-rule)] p-7 rounded-sm shadow-2xl">
        <div className="flex items-start justify-between mb-5">
          <div>
            <p className="ed-overline mb-1">{property.name}</p>
            <h2 className="font-serif text-2xl text-[var(--ed-ink)]">Blocked dates</h2>
          </div>
          <button data-testid="dates-close" onClick={onClose} className="text-[var(--ed-ink-muted)] hover:text-[var(--ed-ink)] p-1"><X size={20} /></button>
        </div>
        <p className="text-[var(--ed-ink-soft)] text-sm mb-6">Click any date to toggle it as blocked (booked, owner stay, maintenance). Selected dates appear in emerald.</p>
        <DateToggleCalendar selected={dates} onToggle={toggle} />
        <div className="flex items-center justify-between mt-6 pt-5 border-t border-[var(--ed-rule)]">
          <span data-testid="dates-count" className="text-[var(--ed-ink-muted)] text-sm">{dates.size} date{dates.size !== 1 ? "s" : ""} blocked</span>
          <div className="flex gap-2">
            <button onClick={onClose} className="text-[0.65rem] uppercase tracking-[0.22em] px-4 py-2.5 border border-[var(--ed-rule)] hover:border-[var(--ed-ink)] text-[var(--ed-ink-soft)] hover:text-[var(--ed-ink)] rounded-sm">Cancel</button>
            <button data-testid="dates-save" onClick={onSave} disabled={saving} className="text-[0.65rem] uppercase tracking-[0.22em] px-5 py-2.5 bg-[var(--ed-emerald)] text-white hover:bg-[var(--ed-emerald-deep)] rounded-sm disabled:opacity-50">
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
